import React, { useEffect, useMemo, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { AddMediaPanel } from '@bingads-webui-campaign-react/asset-library-shared-add-media-layout';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { useJsDataCallback } from '@bingads-webui-cc-react/js-data-rest';
import {
  withAssetLibraryServicesLoaded,
} from '@bingads-webui-campaign-react/asset-library-services';
import {
  withCcJsDataContextProvider,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { withScope } from '@bingads-webui-react/scope-service';
import { FolderAssetType, removeAssetLibrarySurveyTriggerFlag, AssetLibrarySurveyTriggerFlag } from '@bingads-webui-campaign-react/asset-library-model';
import { AssetLibraryCreativeEditor } from '@bingads-webui-campaign-react/asset-library-creative-editor';

import { getAddImageConfig, VIEWS } from './view-config';

const DEFAULT_VIEW = VIEWS.LandingPage;

const ExternalEntityType = {
  XandrAudienceAdsAdvertiser: 15,
};

const isValidXandrAccount = (account) => {
  if (account && account.ExternalAccountIds && account.ExternalAccountIds.length > 0) {
    const externalAccountId = _.find(account.ExternalAccountIds, item => item.Type === ExternalEntityType.XandrAudienceAdsAdvertiser);
    return externalAccountId && externalAccountId.Id !== null && externalAccountId.Id !== undefined;
  }
  return false;
};

const AddImageCrudAdComponentInternal = (props) => {
  const {
    dataService,
    ianaTimeZone,
    assetLibraryUserPreferences,
    assetLibraryAccountPreferences,
    logger,
    assetLibraryConfig,
    isAssetLibraryAIGCMVPEnabled,
    isLiteLandingPageEnabled,
    isStockImageEnabled,
    isAdStudioEnabled,
    creativeEditorProps,
    eventHandler,
    showPromotionBanner,
    panelTitle,
    panelSubtitle,
    isXandrIntEnabled,
    ...rest
  } = props;

  const [hasLoggedEnterAssetLibrary, setHasLoggedEnterAssetLibrary] = useState(false);

  // eslint-disable-next-line no-restricted-globals
  const { data: account, execute: fetchAccountData } = useJsDataCallback(jsData => (isXandrIntEnabled && rest.aid ? jsData.Account.find(rest.aid) : Promise.resolve()), [rest.aid, isXandrIntEnabled]);
  const isXandrIntegrationEnabled = useMemo(() => (isXandrIntEnabled ? isValidXandrAccount(account) : false), [account, isXandrIntEnabled]);

  useEffect(() => () => {
    removeAssetLibrarySurveyTriggerFlag(AssetLibrarySurveyTriggerFlag.Picker);
  }, []);

  useEffect(() => {
    if (isAssetLibraryAIGCMVPEnabled) {
      setClarityTag({ AssetLibraryCopilot: 'true' });
    }
  }, [isAssetLibraryAIGCMVPEnabled]);

  useEffect(() => {
    if (rest.isOpen && !hasLoggedEnterAssetLibrary && eventHandler && _.isFunction(eventHandler.onEnterAssetLibrary)) {
      eventHandler.onEnterAssetLibrary();
      setHasLoggedEnterAssetLibrary(true);
    }
  }, [rest.isOpen, eventHandler, hasLoggedEnterAssetLibrary]);


  useEffect(() => {
    if (rest.isOpen && !account) {
      fetchAccountData();
    }
  }, [rest.isOpen, account, fetchAccountData]);

  assetLibraryConfig.assetLibraryUserPreferences = assetLibraryUserPreferences;
  assetLibraryConfig.assetLibraryAccountPreferences = assetLibraryAccountPreferences;
  assetLibraryConfig.logger = logger;
  assetLibraryConfig.ianaTimeZone = ianaTimeZone;

  return (
    rest.isOpen && creativeEditorProps ?
      <AssetLibraryCreativeEditor
        addMediaConfig={getAddImageConfig({
          isAssetLibraryAIGCMVPEnabled,
          isLiteLandingPageEnabled,
          hideStockImage: true,
          isAdStudioEnabled,
          isXandrIntegrationEnabled: false,
        })}
        assetLibraryConfig={assetLibraryConfig}
        dataService={dataService}
        i18n={rest.i18n}
        isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCMVPEnabled}
        isOpen={rest.isOpen}
        initialView={DEFAULT_VIEW}
        logger={logger}
        {...creativeEditorProps}
        {...rest}
      /> :
      <AddMediaPanel
        assetLibraryDataService={dataService}
        addMediaConfig={getAddImageConfig({
          isAssetLibraryAIGCMVPEnabled,
          isLiteLandingPageEnabled,
          hideStockImage: !isStockImageEnabled,
          isAdStudioEnabled,
          showPromotionBanner,
          panelTitle,
          panelSubtitle,
          isXandrIntegrationEnabled: isXandrIntegrationEnabled && rest.investImageConfig,
        })}
        assetLibraryConfig={assetLibraryConfig}
        logger={logger}
        isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCMVPEnabled}
        {...rest}
        initialView={DEFAULT_VIEW}
        eventHandler={eventHandler}
      />
  );
};

AddImageCrudAdComponentInternal.propTypes = {
  dataService: PropTypes.shape({}).isRequired,
  creativeEditorProps: PropTypes.shape({}),
  eventHandler: PropTypes.shape({
    onEnterAssetLibrary: PropTypes.func,
  }),
  panelTitle: PropTypes.string,
  panelSubtitle: PropTypes.string,
  showPromotionBanner: PropTypes.bool,
  isStockImageEnabled: PropTypes.bool,
  isXandrIntEnabled: PropTypes.bool,
};

AddImageCrudAdComponentInternal.defaultProps = {
  creativeEditorProps: null,
  eventHandler: null,
  panelTitle: '',
  panelSubtitle: '',
  showPromotionBanner: true,
  isStockImageEnabled: true,
  isXandrIntEnabled: false,
};

const AddImageCrudAdComponentInternalWithServices = withAssetLibraryServicesLoaded(withScope(withCcJsDataContextProvider(AddImageCrudAdComponentInternal)), { loggerTag: 'Asset Library Tab' });
export const AddImageCrudAdComponent = (props) => {
  const {
    assetLibraryConfig, i18n, permissions, isAADUser, isLiteLandingPageEnabled, isAssetLibraryAIGCMVPEnabled, eventHandler, serverContext, ...rest
  } = props;
  const [isAssetLibraryAIGCEnabled, setIsAssetLibraryAIGCEnabled] = useState(false);
  const [copilotRegionRestrictedCalled, setCopilotRegionRestrictedCalled] = useState(false);

  const isAIGCForAADUsersEnabled = useMemo(() => _.get(permissions, ['dynamic', 'AIGCForAADUsers'], false), [permissions]);
  const isAIGCForUserEnabled = useMemo(() => !(isAADUser && !isAIGCForAADUsersEnabled), [isAADUser, isAIGCForAADUsersEnabled]);

  // todo: don't pilot AAD user for now, remove this check after the feature is GAed
  const isAssetLibraryAIGCEnabledInner = isAssetLibraryAIGCMVPEnabled === undefined ? isAIGCForUserEnabled : isAssetLibraryAIGCMVPEnabled;

  const isAdStudioEnabled = useMemo(() => _.get(permissions, ['dynamic', 'AdsStudioMVP'], false), [permissions]);
  const isXandrIntEnabled = useMemo(() => _.get(permissions, ['dynamic', 'XandrIntegrationEnabled'], false), [permissions]);

  const { cid, aid } = assetLibraryConfig;

  useEffect(() => {
    if (copilotRegionRestrictedCalled && !isAssetLibraryAIGCEnabledInner) {
      return;
    }
    isCopilotRegionRestricted({
      feature: 'AssetLibrary',
      permissions,
      serverContext,
    }).then((isRestricted) => {
      if (!isRestricted) {
        setIsAssetLibraryAIGCEnabled(isAssetLibraryAIGCEnabledInner);
      }
      setCopilotRegionRestrictedCalled(true);
    });
  }, [aid, cid, copilotRegionRestrictedCalled, isAssetLibraryAIGCEnabledInner, permissions, serverContext]);

  const {
    timeZone,
    preferencesService,
    activityLogger,
    oDataConfig,
    dateFormat,
  } = assetLibraryConfig;

  return (
    <AddImageCrudAdComponentInternalWithServices
      accountTimeZone={timeZone}
      preferenceService={preferencesService}
      activity={activityLogger}
      assetLibraryConfig={assetLibraryConfig}
      oDataConfig={oDataConfig}
      i18n={i18n}
      allowedAssetType={FolderAssetType.Image}
      isAssetLibraryAIGCMVPEnabled={isAssetLibraryAIGCEnabled}
      isLiteLandingPageEnabled={isLiteLandingPageEnabled}
      dateFormat={dateFormat}
      permissions={permissions}
      eventHandler={eventHandler}
      isAdStudioEnabled={isAdStudioEnabled}
      isXandrIntEnabled={isXandrIntEnabled}
      {...rest}
    />
  );
};

AddImageCrudAdComponent.propTypes = {
  assetLibraryConfig: PropTypes.shape({
    timeZone: PropTypes.number,
    preferencesService: PropTypes.shape({}),
    activityLogger: PropTypes.shape({}),
    oDataConfig: PropTypes.shape({}),
    dateFormat: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  eventHandler: PropTypes.shape({
    onEnterAssetLibrary: PropTypes.func,
  }),
  isAADUser: PropTypes.bool,
};

AddImageCrudAdComponent.defaultProps = {
  eventHandler: null,
  isAADUser: false,
};
